import { Component, EventEmitter, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CreateRestService } from 'app/services/create.rest.service';
import { concatMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { AlertDialogComponent } from '../alert-dialog/alert-dialog.component';
import { InformedConsentDialogComponent } from '../informed-consent-dialog/informed-consent-dialog.component';
import { InitializeLoadScreenComponent } from '../initialize-load-screen/initialize-load-screen.component';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  emailAddress: string;
  password: string;
  errorMsg: string;
  inProgress = false;
  supportUrl = environment.externalSupportUrl;
  tou_url = '';
  pp_url = '';

  constructor(
    private router: Router,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private api: CreateRestService,
  ) { }

  ngOnInit() {
    this.api.getConfiguration().subscribe(config => {
      this.tou_url = config['TOU_URL'];
      this.pp_url = config['PP_URL'];
    });

    if (localStorage.getItem(AuthService.authTokenKey)) {
      this.authService.checkForLogin().subscribe(loggedIn => {
        if (loggedIn) {
          this.router.navigate(['dashboard']);
        }
      });
    }
  }

  login(emailAddress, password) {
    this.inProgress = true;

    if (environment.hideConsent === 'true') {
      this.authenticateAndSync(emailAddress, password);
    } else {
      this.dialog
        .open(InformedConsentDialogComponent, {
          width: '800px',
          maxWidth: '90vw',
          disableClose: true,
          closeOnNavigation: false,
        })
        .afterClosed()
        .subscribe(result => {
          if (result === true) {
            this.authenticateAndSync(emailAddress, password);
          } else {
            this.inProgress = false;
          }
        });
    }
  }

  getLink(envVar: string) {
    return environment.applySubstitutions(environment[envVar]);
  }

  getMobileLink() {
    return environment.mobileUrl === '{{MOBILE_URL}}' ?  `https://${window.location.host}/mobile` : environment.mobileUrl;
  }

  authenticateAndSync(emailAddress: string, password: string) {
    this.errorMsg = '';

    this.authService
    .login(emailAddress, password)
    .pipe(concatMap(() => this.authService.checkForLogin()))
    .subscribe(
      () => {
        this.dialog
          .open(InitializeLoadScreenComponent, {
            width: '500px',
            maxWidth: '90vw',
            disableClose: true,
            closeOnNavigation: false,
            data: {
              sync: true,
              autoClose: true,
              title: 'Welcome to the FitForce Planner',
              message: `We're initializing the app… don't close your browser.`,
            },
          })
          .afterClosed()
          .subscribe(() => {
            this.inProgress = false;
            this.router.navigate(['dashboard']);
          });
      },
      err => {
        this.inProgress = false;
        if (err.status === 403) {
          const resendEmailEmitter = new EventEmitter();
          resendEmailEmitter.subscribe(() => {
            this.authService.resendVerificationEmail(this.emailAddress).subscribe(() => {
              this.snackBar.open('Verification email sent', 'Dismiss', { duration: 5000 });
            });
          });

          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Account not Verified',
              content: 'Please check your email for an account verification message and follow the instructions.',
              showSecondaryAction: true,
              secondaryActionText: 'Re-send Email',
              secondaryActionMessage: 'Didn\'t get the email? Click the button below to re-send the verification email.',
              secondaryAction: resendEmailEmitter,
            },
          });
        } else if (err.status === 404) {
          this.errorMsg = 'Incorrect email address/password combination.';
        } else if (err.status === 423) {
          this.errorMsg = 'Account blocked due to failed login attempts. Please reset your password to gain access to your account.';
        } else {
          this.errorMsg = 'Server error.';
        }
      },
    );
  }
}
